import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import type { Graph3DPhysics } from '@/components/map3d/Graph3DView';

export interface EdgeStateSettings {
  opacity: number;
  thickness: number;
}

export interface ComplexityColors {
  ideal: string;      // 1-3 notes
  complex: string;    // 4-5 notes
  tooMany: string;    // 6+ notes
}

export interface GraphSettings {
  // Colors for scent families (overrides defaults)
  scentFamilyColors: Record<string, { light: string; dark: string }>;
  
  // Node opacity levels
  nodeOpacity: number;
  nodeHoverOpacity: number;
  nodeDimmedOpacity: number;
  specificDimmedOpacity: number; // Dimmed opacity for non-selected specific aromatics
  
  // Node border widths
  nodeBorderWidth: number;         // Default border width for nodes
  nodeSelectedBorderWidth: number; // Border width for selected nodes
  
  // Node glow settings
  nodeHoveredGlow: number;   // Glow intensity for hovered nodes (stdDeviation)
  nodeSelectedGlow: number;  // Glow intensity for selected nodes (stdDeviation)
  
  // Edge glow settings
  marriesEdgeGlow: number;   // Glow intensity for Marries Well With edges
  blendsEdgeGlow: number;    // Glow intensity for Blends Well With edges
  
  // Edge states - each has opacity and thickness
  edgeDefault: EdgeStateSettings;     // Default state (no selection)
  edgeHighlighted: EdgeStateSettings; // Hover or connection between two selected nodes
  edgeSelected: EdgeStateSettings;    // Edges of selected nodes to non-selected nodes
  edgeDimmed: EdgeStateSettings;      // Edges between non-selected nodes when in selection mode
  
  // Radii
  innerRadius: number;
  outerRadius: number;
  familyRimWidth: number;
  specificInnerRadius: number;
  specificOuterRadius: number;
  
  // Edge bundling
  edgeTension: number;
  edgeStartStrength: number; // How strongly edges start toward center (0 = along arc, 1 = fully toward center)

  // Atlas 2D ↔ 3D transition
  morphDotDurationMs: number;
  morphArcDurationMs: number;
  morphEdgeCurveDurationMs: number;
  morphBackgroundDimDurationMs: number;
  morphMoveEdges: boolean;
  
  // Complexity indicator colors
  complexityColors: ComplexityColors;

  // OKLCH saturation scale (Atlas v4) — separate per mode
  satScaleLight: number;
  satScaleDark: number;

  // Shared 3D graph presentation settings, edited by admins and applied to all visitors.
  graph3DPhysics: Graph3DPhysics;
}

export const DEFAULT_COMPLEXITY_COLORS: ComplexityColors = {
  ideal: '#7DBE8E',    // Green - 1-3 notes
  complex: '#E8C463',  // Amber - 4-5 notes
  tooMany: '#E07B5A',  // Orange/red - 6+ notes
};

export const DEFAULT_GRAPH_SETTINGS: GraphSettings = {
  scentFamilyColors: {},
  nodeOpacity: 1,
  nodeHoverOpacity: 1,
  nodeDimmedOpacity: 0.3,
  specificDimmedOpacity: 0.4,
  // Node border widths
  nodeBorderWidth: 1,
  nodeSelectedBorderWidth: 2,
  // Node glow settings
  nodeHoveredGlow: 3,
  nodeSelectedGlow: 3,
  // Edge glow settings
  marriesEdgeGlow: 3,
  blendsEdgeGlow: 2,
  // Edge states
  edgeDefault: { opacity: 0.15, thickness: 1 },
  edgeHighlighted: { opacity: 0.8, thickness: 2 },
  edgeSelected: { opacity: 0.45, thickness: 1.5 },
  edgeDimmed: { opacity: 0.05, thickness: 0.5 },
  // Radii
  innerRadius: 180,
  outerRadius: 280,
  familyRimWidth: 7,
  specificInnerRadius: 281,
  specificOuterRadius: 346,
  edgeTension: 0.85,
  edgeStartStrength: 0.5,
  morphDotDurationMs: 900,
  morphArcDurationMs: 850,
  morphEdgeCurveDurationMs: 900,
  morphBackgroundDimDurationMs: 420,
  morphMoveEdges: true,
  // Complexity colors
  complexityColors: DEFAULT_COMPLEXITY_COLORS,
  // OKLCH saturation scale (Atlas v4)
  satScaleLight: 0.85,
  satScaleDark: 1.0,
  graph3DPhysics: {
    chargeStrength: 120,
    linkDistance: 40,
    panSpeed: 0.15,
    autoRotate: true,
    autoRotateSpeed: 0.4,
    nodeOpacityIdle: 1,
    nodeOpacitySelected: 1,
    nodeOpacityDim: 0.15,
    nodeOpacityHover: 1,
    nodeDimmedCoverage: 0.5,
    edgeOpacityIdle: 0.35,
    edgeOpacitySelected: 0.9,
    edgeOpacityDim: 0.06,
    edgeOpacityHover: 0.85,
    edgeThicknessIdle: 0.4,
    edgeThicknessSelected: 1.6,
    edgeThicknessDim: 0.4,
    edgeThicknessHover: 1.6,
    glowIntensity: 0.9,
    glowRadius: 7,
    rearLabelFade: 0.35,
    rearFadeStart: 0,
    rearFadeDistance: 160,
    rearFadeAffectsGeometry: true,
    hoverDimInDurationMs: 150,
    hoverDimOutDurationMs: 360,
    interactionTargetScale: 1.8,
  },
};

// Helper to get complexity color based on count
export function getComplexityColor(count: number, colors: ComplexityColors): string | undefined {
  if (count === 0) return undefined; // Use default gray
  if (count <= 3) return colors.ideal;
  if (count <= 5) return colors.complex;
  return colors.tooMany;
}

const SETTINGS_ID = 'graph_settings';

export function useAppSettings() {
  const { session } = useAuth();
  const [settings, setSettings] = useState<GraphSettings>(DEFAULT_GRAPH_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('id', SETTINGS_ID)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') { // Not found is okay
            console.error('Error fetching settings:', error);
          }
        } else if (data?.value) {
          // Merge with defaults to ensure all keys exist (deep merge for nested objects)
          const loadedSettings = data.value as unknown as Partial<GraphSettings>;
          setSettings({
            ...DEFAULT_GRAPH_SETTINGS,
            ...loadedSettings,
            // Deep merge nested objects
            complexityColors: {
              ...DEFAULT_COMPLEXITY_COLORS,
              ...(loadedSettings.complexityColors || {}),
            },
            graph3DPhysics: {
              ...DEFAULT_GRAPH_SETTINGS.graph3DPhysics,
              ...(loadedSettings.graph3DPhysics || {}),
            },
          });
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Save settings (admin only)
  const saveSettings = useCallback(async (newSettings: GraphSettings) => {
    if (!session) {
      toast.error('You must be logged in to save settings');
      return false;
    }

    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          id: SETTINGS_ID,
          value: newSettings as unknown as Json,
          updated_at: new Date().toISOString(),
        });

      if (error) {
        console.error('Error saving settings:', error);
        toast.error('Failed to save settings');
        return false;
      }

      setSettings(newSettings);
      toast.success('Settings saved');
      return true;
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
      return false;
    }
  }, [session]);

  // Update a single setting locally (for live preview)
  const updateSetting = useCallback(<K extends keyof GraphSettings>(
    key: K,
    value: GraphSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update a scent family color
  const updateScentFamilyColor = useCallback((
    family: string,
    colors: { light: string; dark: string }
  ) => {
    setSettings(prev => ({
      ...prev,
      scentFamilyColors: {
        ...prev.scentFamilyColors,
        [family]: colors,
      },
    }));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_GRAPH_SETTINGS);
  }, []);

  return {
    settings,
    loading,
    saveSettings,
    updateSetting,
    updateScentFamilyColor,
    resetToDefaults,
  };
}
